import type {SingleTechnicalDataGroup} from '@volkswagen-onehub/car-technical-details-bff';
import React from 'react';
import {
  ModelFilterNames,
  type ModelFilterTechnicalDataGroup,
  type VictorModelFilter,
} from '@volkswagen-onehub/car-technical-details-bff';
import {resolveDataGroupName} from '../../utils/resolveDataGroupName';
import {useTrackingService} from '../../utils/useTrackingService';
import {getDescriptions} from './getDescriptions';
import {
  Breakpoints,
  Table,
  TableBody,
  TableHead,
  TableCell,
  TableRow,
  TableHeader,
  TableCaption,
  Text,
} from '@volkswagen-onehub/components-core';
import {getKeyByDiscriminators} from './getKeyByDiscriminators';
import {useTranslate} from '../../utils/translate';
import {RowHeader} from '../RowHeader';

export interface MultiTableProps {
  group: ModelFilterTechnicalDataGroup & {filterTitle?: string};
}

function sortGroup(
  group: ModelFilterTechnicalDataGroup,
  discriminators: {description: string; key: string}[],
) {
  const orderedGroup = {
    ...group,
    variations: group.variations.sort((a, b) => {
      for (const discriminator of discriminators) {
        const valueA: string = (a.modelFilters[discriminator.key] || [])
          .map((filter) => filter.text)
          .sort()
          .join(', ');
        const valueB: string = (b.modelFilters[discriminator.key] || [])
          .map((filter) => filter.text)
          .sort()
          .join(', ');

        const compare = (valueA || '').localeCompare(valueB || '');
        if (compare !== 0) {
          return compare;
        }
      }
      return 0;
    }),
  };
  return orderedGroup;
}

export const MultiTable = (props: MultiTableProps): JSX.Element => {
  const {group: groupWithEmptyColumns} = props;
  const {filterTitle} = groupWithEmptyColumns;

  const translate = useTranslate();
  const groupName = resolveDataGroupName(props.group, translate);
  const tracking = useTrackingService();

  // Columns are empty because of missing group data. They are either filterd out or hidden here.
  const group = {
    ...groupWithEmptyColumns,
    variations: groupWithEmptyColumns.variations.filter(
      (v) => v.values.length > 0,
    ),
  };

  const descriptions = getDescriptions(group, translate);

  const discriminators = getDiscriminators(group);

  const orderedGroup = sortGroup(group, discriminators);

  const body = descriptions
    .filter(
      (d) =>
        !discriminators.find((dis) => dis.description === d.description.label),
    )

    .map((d, index) => {
      const columns = orderedGroup.variations.map((variation, varIndex) => {
        const variationValue = variation.values.find((v) => v.key === d.key);
        if (!variationValue) {
          const key = d + '_' + varIndex + '_' + index;
          return <TableCell key={key}></TableCell>;
        }
        const value = variationValue.value;
        const key = getKeyByDiscriminators(variation.modelFilters);

        return (
          <TableCell key={variationValue.key + ' ' + key}>
            <Text hyphens="none">{value}</Text>
          </TableCell>
        );
      });

      return (
        <TableRow key={d.key}>
          <TableHeader emphasized>
            <RowHeader row={d.description} />
          </TableHeader>
          {columns}
        </TableRow>
      );
    });

  const tableCaption = filterTitle ? `${groupName} ${filterTitle}` : groupName;

  return (
    <Table
      navigation={group.variations.length > 1 ? 'auto' : 'none'}
      columns={{
        [Breakpoints.default]: 2,
        [Breakpoints.b960]: 3,
        [Breakpoints.b1280]: 4,
      }}
      fixedHeaderColumn
      solidHeaderColumn
      onNavigateNext={() =>
        tracking.trackSubNavigationClick(groupName, group.filterTitle || 'All')
      }
      onNavigatePrevious={() =>
        tracking.trackSubNavigationClick(groupName, group.filterTitle || 'All')
      }
    >
      <TableCaption>{tableCaption}</TableCaption>
      <TableHead>
        <ColumnHeader group={group} />
        <AdditionalHeader group={group} />
      </TableHead>
      <TableBody>{body}</TableBody>
    </Table>
  );
};

const AdditionalHeader = (props: MultiTableProps) => {
  const {group} = props;
  const additional = getAdditional(group);

  const additionalRow = additional.map((d, idx) => {
    const additionalCells = group.variations
      .filter((v) => v.additionalFilters[d.key].length > 0)
      .map((variation) => {
        const filters = variation.additionalFilters[d.key];
        const variationValue = {
          value: filters
            .map((d) => d.text)
            .sort()
            .join(', '),
          description: filters[0].localizedName,
        };

        const value = variationValue.value;
        const key =
          variationValue.description +
          getKeyByDiscriminators(variation.modelFilters);
        const hyphens = isNoHyphenization(filters[0]) ? 'none' : undefined;
        return (
          <TableHeader key={key} emphasized>
            <Text bold hyphens={hyphens}>
              {value}
            </Text>
          </TableHeader>
        );
      });

    return (
      // biome-ignore lint/suspicious/noArrayIndexKey: no key available
      <TableRow key={d.key + ' ' + idx}>
        <TableHeader emphasized>
          <Text bold wordBreak>
            {d.description}
          </Text>
        </TableHeader>
        {additionalCells}
      </TableRow>
    );
  });

  return <>{additionalRow}</>;
};

const ColumnHeader = (props: {group: ModelFilterTechnicalDataGroup}) => {
  const {group} = props;
  const discriminators = getDiscriminators(group);

  const columnHeader = discriminators.map((d, idx) => {
    const columnHeaders = group.variations.map((variation) => {
      const key = getKeyByDiscriminators(variation.modelFilters);

      const filters = variation.modelFilters[d.key];
      if (!filters) {
        return <TableHeader key={key} emphasized />;
      }

      const variationValue = {
        value: filters
          .map((d) => d.text)
          .sort()
          .join(', '),
        description: variation.modelFilters[d.key][0].localizedName,
      };

      const value = variationValue.value;
      const hyphens = isNoHyphenization(filters[0]) ? 'none' : undefined;
      return (
        <TableHeader key={key} emphasized>
          <Text bold wordBreak hyphens={hyphens}>
            {value}
          </Text>
        </TableHeader>
      );
    });

    return (
      // biome-ignore lint/suspicious/noArrayIndexKey: no key available
      <TableRow key={d.key + ' ' + idx}>
        <TableHeader emphasized>
          <Text bold wordBreak>
            {d.description}
          </Text>
        </TableHeader>
        {columnHeaders}
      </TableRow>
    );
  });

  return <>{columnHeader}</>;
};

function isNoHyphenization(filter: VictorModelFilter) {
  return filter.type === ModelFilterNames.EquipmentLine;
}

function getAdditional(group: ModelFilterTechnicalDataGroup) {
  return findFilterNames(
    group.additionalModelFilterTypes.filter(
      (key) => key === ModelFilterNames.EquipmentLine,
    ),
    group.variations,
    'additionalFilters',
  );
}

function getDiscriminators(group: ModelFilterTechnicalDataGroup) {
  return findFilterNames(
    group.discriminatingModelFilterTypes,
    group.variations,
    'modelFilters',
  );
}

function findFilterNames(
  filterTypes: string[],
  variations: SingleTechnicalDataGroup[],
  variationFieldNameForFilters: 'additionalFilters' | 'modelFilters',
) {
  return filterTypes
    .map((key) => {
      const variationWithFilter = variations.find((variation) =>
        Boolean(variation[variationFieldNameForFilters][key]),
      );

      if (!variationWithFilter) {
        return null;
      }

      const filters = variationWithFilter[variationFieldNameForFilters];
      const filter = filters[key];
      const filterWithName = filter?.find((entry) =>
        Boolean(entry.localizedName),
      );
      if (!filterWithName) {
        return null;
      }

      return {
        key,
        description: filterWithName.localizedName,
      };
    })
    .filter(notEmpty);
}

function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
  return value !== null && value !== undefined;
}
